import { useEffect, useMemo, useState } from 'react'
import { Spinner } from '../../../components/ui/Spinner'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { DataBR } from '../../../components/ui/DataBR'

// Anuidade e joia do clube (migration 019): gera uma conta a receber por atirador marcado.
// Quem ja tem a anuidade do ano (ou a joia) vem bloqueado - o banco tambem impede em dobro.

type Referencia = 'anuidade' | 'joia'

interface LinhaAtirador {
  idatirador: number
  nome: string
  cpf: string | null
  numero_cr: string | null
  clube_nome: string
  idconta: number | null
  status: 'aberta' | 'vencida' | 'paga' | null
  valor: number | null
  ano: number | null
}

interface Resultado {
  criados: Array<{ idatirador: number; nome: string; idconta: number }>
  ignorados: Array<{ idatirador: number; nome: string; motivo: string }>
}

const brl = (v: number | null | undefined) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
// erros lancados na API vem com o texto em `message` (e `error` = "Bad Request"/"Conflict"...)
const erroApi = (err: any, padrao: string) => err?.response?.data?.message || err?.response?.data?.error || padrao
const STATUS = { aberta: 'Em aberto', vencida: 'Vencida', paga: 'Paga' } as const

function paraNumero(v: string): number | null {
  const t = v.trim()
  if (!t) return null
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t)
  return Number.isFinite(n) ? n : null
}

export function Anuidades({ onAlert, onVerContas }: {
  onAlert: (a: { type: 'success' | 'error'; message: string }) => void
  onVerContas: () => void
}) {
  const anoAtual = new Date().getFullYear()
  const [ref, setRef] = useState<Referencia>('anuidade')
  const [ano, setAno] = useState(anoAtual)
  const [valor, setValor] = useState('')
  const [vencimento, setVencimento] = useState(`${anoAtual}-01-31`)
  const [busca, setBusca] = useState('')
  const [linhas, setLinhas] = useState<LinhaAtirador[]>([])
  const [carregando, setCarregando] = useState(false)
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [gerando, setGerando] = useState(false)
  const [resultado, setResultado] = useState<Resultado | null>(null)

  async function carregar() {
    setCarregando(true)
    try {
      const { data } = await api.get<{ data: LinhaAtirador[] }>('/financeiro/anuidades/atiradores', { params: { referencia: ref, ano } })
      setLinhas(data.data)
      // marca todos que ainda nao tem a cobranca
      setMarcados(new Set(data.data.filter(l => !l.idconta).map(l => l.idatirador)))
    } catch (err) {
      onAlert({ type: 'error', message: erroApi(err, 'Erro ao carregar os atiradores') })
    } finally { setCarregando(false) }
  }

  useEffect(() => { setResultado(null); carregar() }, [ref, ano])
  // vencimento padrao acompanha o ano escolhido
  useEffect(() => { setVencimento(v => `${ano}${v.slice(4)}`) }, [ano])

  const visiveis = useMemo(() => {
    const t = busca.trim().toUpperCase()
    return t ? linhas.filter(l => l.nome.toUpperCase().includes(t)) : linhas
  }, [linhas, busca])
  const livres = visiveis.filter(l => !l.idconta)
  const todos = livres.length > 0 && livres.every(l => marcados.has(l.idatirador))
  const qtdJa = linhas.filter(l => l.idconta).length
  const nomeRef = ref === 'anuidade' ? `anuidade ${ano}` : 'joia'

  function toggle(id: number) {
    setMarcados(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }

  async function gerar() {
    const v = paraNumero(valor)
    if (!v || v <= 0) { onAlert({ type: 'error', message: 'Informe o valor' }); return }
    if (!vencimento) { onAlert({ type: 'error', message: 'Informe o vencimento' }); return }
    const ids = [...marcados]
    if (!ids.length) { onAlert({ type: 'error', message: 'Marque ao menos um atirador' }); return }
    if (!window.confirm(`Gerar ${nomeRef} de ${brl(v)} para ${ids.length} atirador(es), vencimento ${vencimento.split('-').reverse().join('/')}?\n\nCada um vira uma conta a receber.`)) return
    setGerando(true)
    try {
      const { data } = await api.post<{ data: Resultado }>('/financeiro/anuidades/gerar', { referencia: ref, ano, valor: v, vencimento, idatiradores: ids })
      setResultado(data.data)
      onAlert({ type: 'success', message: `${data.data.criados.length} cobranca(s) de ${nomeRef} gerada(s)${data.data.ignorados.length ? `, ${data.data.ignorados.length} ignorada(s)` : ''}.` })
      carregar()
    } catch (err) {
      onAlert({ type: 'error', message: erroApi(err, 'Erro ao gerar as cobrancas') })
    } finally { setGerando(false) }
  }

  return (
    <div className="space-y-4" data-anuidades>
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-3">
        <p className="text-sm text-compumaq-textoSuave">
          Gera a anuidade (uma por ano) ou a joia (uma vez so) para os atiradores marcados. Cada cobranca vira uma conta em
          <b> Contas a Receber</b>, onde se da a baixa e se anexa o comprovante. Quem ja tem a cobranca aparece bloqueado.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Cobranca</label>
            <select className={selectClass} aria-label="Cobranca" value={ref} onChange={e => setRef(e.target.value as Referencia)}>
              <option value="anuidade">Anuidade</option>
              <option value="joia">Joia</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Ano</label>
            <select className={selectClass} aria-label="Ano" value={ano} onChange={e => setAno(Number(e.target.value))}>
              {[anoAtual - 1, anoAtual, anoAtual + 1, anoAtual + 2].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Valor (R$)</label>
            <input className={inputClass} aria-label="Valor da cobranca" inputMode="decimal" placeholder="0,00" value={valor} onChange={e => setValor(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Vencimento</label>
            <DataBR className={inputClass} aria-label="Vencimento da cobranca" value={vencimento} onChange={e => setVencimento(e.target.value)} />
          </div>
        </div>
        {ref === 'joia' && <p className="text-xs text-compumaq-textoSuave">A joia e cobrada uma vez por atirador (o ano fica so como referencia).</p>}
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        <div className="flex flex-wrap gap-3 items-end p-4 border-b border-compumaq-dourado/25">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-compumaq-textoSuave mb-1">Buscar atirador</label>
            <input className={inputClass} value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome" />
          </div>
          <div className="text-sm text-compumaq-textoSuave">
            {marcados.size} marcado(s){qtdJa ? ` · ${qtdJa} ja com ${nomeRef}` : ''}
          </div>
          <button onClick={gerar} disabled={gerando || !marcados.size}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50 font-medium">
            {gerando ? 'Gerando...' : `Gerar ${ref === 'anuidade' ? 'anuidade' : 'joia'} (${marcados.size})`}
          </button>
        </div>
        {carregando ? (
          <div className="flex justify-center py-12"><Spinner size="lg" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25 text-xs text-compumaq-textoSuave">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox" aria-label="Marcar todos" checked={todos}
                    onChange={() => setMarcados(prev => {
                      const n = new Set(prev)
                      for (const l of livres) { if (todos) n.delete(l.idatirador); else n.add(l.idatirador) }
                      return n
                    })} />
                </th>
                <th className="text-left px-3 py-2">Atirador</th>
                <th className="text-left px-3 py-2">CR</th>
                <th className="text-left px-3 py-2">{ref === 'anuidade' ? `Anuidade ${ano}` : 'Joia'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {visiveis.map(l => (
                <tr key={l.idatirador} data-atirador={l.idatirador} className={l.idconta ? 'opacity-70' : 'hover:bg-compumaq-preto'}>
                  <td className="px-3 py-2">
                    <input type="checkbox" disabled={!!l.idconta} checked={!l.idconta && marcados.has(l.idatirador)} onChange={() => toggle(l.idatirador)} />
                  </td>
                  <td className="px-3 py-2 text-compumaq-texto">{l.nome}</td>
                  <td className="px-3 py-2 text-compumaq-textoSuave">{l.numero_cr || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {l.idconta && l.status
                      ? <span className={l.status === 'paga' ? 'text-green-600' : l.status === 'vencida' ? 'text-red-600' : 'text-amber-600'}>
                          ja gerada · {STATUS[l.status]} · {brl(l.valor)}
                        </span>
                      : <span className="text-compumaq-textoSuave">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {resultado && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 text-sm space-y-1" data-resultado>
          <p className="font-medium text-compumaq-texto">
            {resultado.criados.length} cobranca(s) gerada(s){resultado.ignorados.length ? ` · ${resultado.ignorados.length} ignorada(s)` : ''}
          </p>
          {resultado.ignorados.length > 0 && (
            <p className="text-amber-600">Ignorados: {resultado.ignorados.map(i => `${i.nome || i.idatirador} (${i.motivo})`).join('; ')}</p>
          )}
          <button onClick={onVerContas} className="text-xs text-compumaq-doradoClaro font-medium underline">Ver em Contas a Receber</button>
        </div>
      )}
    </div>
  )
}
